/** Snake for the line into a full game: a square board, eating gold S's. */

export const SNAKE_CELLS = 16;

export type Cell = [number, number];

export interface Snake {
  /** Head first. */
  body: Cell[];
  dir: Cell;
  /** The turn taken on the next step; turning back on yourself is ignored. */
  next: Cell;
  food: Cell | null;
  over: boolean;
}

const same = (a: Cell, b: Cell) => a[0] === b[0] && a[1] === b[1];

export function newSnake(random = Math.random): Snake {
  const mid = SNAKE_CELLS / 2;
  const body: Cell[] = [
    [mid, mid],
    [mid - 1, mid],
    [mid - 2, mid],
  ];
  return {
    body,
    dir: [1, 0],
    next: [1, 0],
    food: place(body, random),
    over: false,
  };
}

/** A random free cell, or null when the snake fills the board. */
function place(body: Cell[], random: () => number): Cell | null {
  const free: Cell[] = [];
  for (let x = 0; x < SNAKE_CELLS; x++)
    for (let y = 0; y < SNAKE_CELLS; y++)
      if (!body.some((c) => same(c, [x, y]))) free.push([x, y]);
  return free.length ? free[Math.floor(random() * free.length)] : null;
}

export function turn(snake: Snake, dir: Cell): void {
  if (dir[0] === -snake.dir[0] && dir[1] === -snake.dir[1]) return;
  snake.next = dir;
}

export function step(snake: Snake, random = Math.random): void {
  if (snake.over) return;
  snake.dir = snake.next;
  const [x, y] = snake.body[0];
  const head: Cell = [x + snake.dir[0], y + snake.dir[1]];
  const eats = !!snake.food && same(head, snake.food);
  // The tail moves out of the way first, unless the snake grows.
  const rest = eats ? snake.body : snake.body.slice(0, -1);
  const outside = head.some((n) => n < 0 || n >= SNAKE_CELLS);
  if (outside || rest.some((c) => same(c, head))) {
    snake.over = true;
    return;
  }
  snake.body = [head, ...rest];
  if (!eats) return;
  snake.food = place(snake.body, random);
  if (!snake.food) snake.over = true;
}
